import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Modal from 'react-modal';
import {
  RootState,
  fetchRequestCardRequest,
  resetRequestCardSlice,
  updateRequestCardRequest,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import Spinner from './Spinner';
import CommanModal from './CommanModal';
import React from 'react';
import { logBrazeCustomEvent } from '../config/braze';

interface RequestNewCardProps {
  isRequestCardOpen: boolean;
  setIsRequestCardOpen: Dispatch<SetStateAction<boolean>>;
}

function RequestNewCard({
  isRequestCardOpen,
  setIsRequestCardOpen,
}: RequestNewCardProps) {
  const dispatch = useDispatch();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountId, customerId } = useSelector(
    (state: RootState) => state.user
  );
  const { loading, requestCardOptions, requestCard, error } = useSelector(
    (state: RootState) => state.requestCard
  );
  const { entries, assets } = contentfulData;
  const { rb_request_new_card, rb_error } = entries;
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false);
  const [hasErrOccured, setHasErrOccured] = useState(false);

  useEffect(() => {
    dispatch(
      fetchRequestCardRequest({
        accountId,
        customerId,
      })
    );
  }, []);

  useEffect(() => {
    if (error) {
      setIsError(true);
      setHasAlreadySubmitted(false);
      setHasErrOccured(true);
    }
    if (error && error?.title === 'Request Card Failed') {
      setHasAlreadySubmitted(true);
      setIsError(false);
      setHasErrOccured(true);
    }
  }, [error, hasAlreadySubmitted, isError]);

  useEffect(() => {
    if (requestCard) {
      setIsSuccess(true);
    }
  }, [requestCard]);

  useEffect(() => {
    if (!loading && !error && isRequestCardOpen && !isSuccess) {
      logBrazeCustomEvent('viewed_request_replacement_card');
    }
  }, [loading, error, isRequestCardOpen, isSuccess]);

  const handleSubmit = async () => {
    logBrazeCustomEvent('action_requested_replacement_card');
    try {
      await dispatch(
        updateRequestCardRequest({
          accountId: accountId,
          customerId: customerId,
          requestBody: {
            assignmentId: requestCardOptions?.assignmentId,
            requestsAllCards: false,
            customers: [
              {
                customerId: customerId,
                reason: 'request for new cards',
                deliveryMethodType: 'NORMAL',
              },
            ],
          },
        })
      );
      if (requestCard?.status === 'APPROVED') {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('Error updating request card:', error);
    }
  };

  const handleCancel = () => {
    setIsRequestCardOpen(false);
  };

  const BoldText = ({ text }: { text: string }) => {
    const boldPhrases = [
      'Important:',
      'call us immediately and do not submit this request.',
      'Submit',
      'Important:',
      'appelez-nous immédiatement et ne soumettez pas cette demande.',
      'Soumettre',
    ];
    const regex = new RegExp(`(${boldPhrases.join('|')})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          boldPhrases.some(
            (phrase) => phrase.toLowerCase() === part.toLowerCase().trim()
          ) ? (
            <span key={index} className="font-bold">
              {part}
            </span>
          ) : (
            <React.Fragment key={index}>{part}</React.Fragment>
          )
        )}
      </>
    );
  };

  return (
    <>
      {!loading && !error && isRequestCardOpen && !isSuccess && (
        <CommanModal
          isOpen={isRequestCardOpen}
          width="md:max-w-[630px] max-w-full"
          height="md:h-auto h-[100vh]"
          jsxContent={
            <>
              <div
                className="mb-[30px] text-[#1F1F1F] text-[22px] font-medium"
                aria-label={rb_request_new_card?.requestNewCard?.title}
              >
                {renderDescriptionParagraphs(
                  rb_request_new_card?.requestNewCard?.title
                )}
              </div>
              <div
                className="mb-[30px]"
                aria-label={rb_request_new_card?.requestNewCard?.description}
              >
                {renderDescriptionParagraphs(
                  rb_request_new_card?.requestNewCard?.description
                )}
              </div>
            </>
          }
          onClose={() => {
            handleCancel();
          }}
          buttonText={rb_request_new_card?.requestNewCard?.cancelBtn}
          cardStatus={true}
          closeIcon={false}
          extraButtonText={rb_request_new_card?.requestNewCard?.submitBtn}
          onExtraButtonClick={() => {
            handleSubmit();
          }}
          buttonClassName="md:relative md:flex-row fixed bottom-0 w-full md:w-3/4 left-0 p-5 md:p-0 border md:border-none bt-[#E9E9E9] bg-[#FAFAFA] md:bg-white"
          customStyle={'fixed bottom-0 width-full left-0 p-5'}
        />
      )}

      {loading && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Spinner />
        </div>
      )}

      {!loading && isSuccess && (
        <Modal
          isOpen={isSuccess}
          contentLabel="requestCardSuccessModal"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.device_recogonized_success_popup_icon}
              alt="successIcon"
              className="w-20 h-20"
            />
            <h3
              aria-label={
                rb_request_new_card?.requestNewCard?.confirmationPopup?.title
              }
              className="text-[#1F1F1F] text-[22px] font-[650] leading-[28px] tracking-[0.077px] text-center"
            >
              {rb_request_new_card?.requestNewCard?.confirmationPopup?.title}
            </h3>
            <p
              aria-label={
                rb_request_new_card?.requestNewCard?.confirmationPopup
                  ?.description
              }
              className="text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px] text-center"
            >
              {rb_request_new_card?.requestNewCard?.confirmationPopup?.description
                ?.split('\n')
                .map((line: string, index: number) => (
                  <span key={index}>
                    {line} <br />
                  </span>
                ))}
            </p>
            <p
              aria-label={`${
                rb_request_new_card?.requestNewCard?.confirmationPopup
                  ?.confirmationNumber
              } ${requestCard?.confirmationNumber}`}
              className="text-[#1F1F1F] bg-gray-100 w-full text-[16px] font-[500] leading-[20px] tracking-[-0.051px] text-center"
            >
              {
                rb_request_new_card?.requestNewCard?.confirmationPopup
                  ?.confirmationNumber
              }{' '}
              {requestCard?.confirmationNumber}
            </p>
            <button
              aria-label={
                rb_request_new_card?.requestNewCard?.unavailable?.primaryBtn
              }
              type="button"
              onClick={() => {
                setIsSuccess(false);
                setIsRequestCardOpen(false);
                dispatch(resetRequestCardSlice());
              }}
              className="text-center flex-1 items-center align-center text-white px-[20px] py-[12px] bg-primary-red w-full"
            >
              {rb_request_new_card?.requestNewCard?.unavailable?.primaryBtn}
            </button>
          </div>
        </Modal>
      )}

      {hasErrOccured && (
        <CommanModal
          isOpen={isError || hasAlreadySubmitted}
          onClose={() => {
            setHasErrOccured(false);
            setIsRequestCardOpen(false);
            dispatch(resetRequestCardSlice());
          }}
          cardStatus={true}
          jsxContent={
            <div className="text-center">
              <div
                aria-label={
                  error &&
                  error?.title === 'There are no cards eligible to be requested'
                    ? rb_request_new_card?.requestNewCard?.alreadySubmittedPopup
                        .title
                    : (rb_error.requestNewCard[
                        error?.title?.toUpperCase()?.replace(/\s+/g, '_')
                      ]?.title ??
                      error?.title ??
                      rb_request_new_card?.requestNewCard?.unavailable?.title)
                }
                className="mb-4 text-[#1F1F1F] text-[22px] font-medium"
              >
                {error &&
                error?.title === 'There are no cards eligible to be requested'
                  ? rb_request_new_card?.requestNewCard?.alreadySubmittedPopup
                      .title
                  : (rb_error.requestNewCard[
                      error?.title?.toUpperCase()?.replace(/\s+/g, '_')
                    ]?.title ??
                    error?.title ??
                    rb_request_new_card?.requestNewCard?.unavailable?.title)}
              </div>
              <p
                aria-label={
                  error &&
                  error?.title === 'There are no cards eligible to be requested'
                    ? rb_request_new_card?.requestNewCard?.alreadySubmittedPopup
                        .description
                    : (rb_error.requestNewCard[
                        error?.title?.toUpperCase()?.replace(/\s+/g, '_')
                      ]?.detail ??
                      error?.detail ??
                      rb_request_new_card?.requestNewCard?.unavailable
                        ?.description)
                }
                className="mb-[40px]"
              >
                {error &&
                error?.title === 'There are no cards eligible to be requested'
                  ? rb_request_new_card?.requestNewCard?.alreadySubmittedPopup
                      .description
                  : (rb_error.requestNewCard[
                      error?.title?.toUpperCase()?.replace(/\s+/g, '_')
                    ]?.detail ??
                    error?.detail ??
                    rb_request_new_card?.requestNewCard?.unavailable
                      ?.description)}
              </p>
            </div>
          }
          buttonText={
            rb_request_new_card?.requestNewCard?.unavailable?.primaryBtn
          }
          closeIcon={false}
        />
      )}
    </>
  );
}

export default RequestNewCard;
